import { useMemo } from 'react';
import { Link } from 'react-router-dom';

import type { Tag, TagResumo } from '@/api/tags';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import { TagPainel } from './TagPainel';

interface TagsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** O que identifica o registro no título ("Tags — 0001234-56.2024.8.26.0100"). */
  identificador: string;
  /** As tags que o registro tem agora. */
  aplicadas: TagResumo[];
  /** Novo conjunto (cada toque aplica na hora; "Concluir" só fecha). */
  onChange: (tags: TagResumo[]) => void;
  somenteLeitura?: boolean;
  /** O atalho para a tela de Tags. Desligado onde sair da tela perde trabalho (envio de documento). */
  mostrarLinkTela?: boolean;
}

const resumo = (tag: Tag): TagResumo => ({
  id: tag.id,
  nome: tag.nome,
  cor: tag.cor,
  categoria_id: tag.categoria_id,
});

/** Diálogo de tags de um registro, fiel ao print do escritório (PLANO_TAGS §1 e §6). */
export function TagsDialog({
  open,
  onOpenChange,
  identificador,
  aplicadas,
  onChange,
  somenteLeitura,
  mostrarLinkTela = true,
}: TagsDialogProps) {
  const marcadas = useMemo(() => new Set(aplicadas.map((t) => t.id)), [aplicadas]);

  const alternar = (tag: Tag) => {
    onChange(marcadas.has(tag.id) ? aplicadas.filter((t) => t.id !== tag.id) : [...aplicadas, resumo(tag)]);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90dvh] w-[calc(100vw-1rem)] max-w-lg flex-col gap-3 p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle className="break-words pr-6">Tags — {identificador}</DialogTitle>
          <DialogDescription>
            {somenteLeitura ? 'Sua delegação nesta conta é só de leitura.' : 'Toque numa tag para aplicar ou remover'}
          </DialogDescription>
        </DialogHeader>
        <div className="-mx-1 min-h-0 flex-1 overflow-y-auto px-1">
          <TagPainel
            marcadas={marcadas}
            onAlternar={alternar}
            onCriada={(tag) => !marcadas.has(tag.id) && onChange([...aplicadas, resumo(tag)])}
            somenteLeitura={somenteLeitura}
          />
        </div>
        <DialogFooter className="gap-2 sm:items-center sm:justify-between">
          {mostrarLinkTela && (
            <Button asChild variant="link" className="h-11 px-0 sm:h-9">
              <Link to="/app/tags" onClick={() => onOpenChange(false)}>
                Abrir a tela de Tags
              </Link>
            </Button>
          )}
          <Button onClick={() => onOpenChange(false)} className="h-11 w-full sm:h-9 sm:w-auto">
            Concluir
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
